import type { Terminal } from '@xterm/xterm';
import { THEMES, type ThemeName } from './themes';

const THEME_KEY = 'terminal.nsi:theme';
const FONT_SIZE_KEY = 'terminal.nsi:fontSize';
const PANEL_FONT_KEY = 'terminal.nsi:panelFontSize';
const PANEL_HEIGHT_KEY = 'terminal.nsi:panelHeight';
const COLORS_KEY = 'terminal.nsi:colors';
const PANEL_WIDTH_KEY = 'terminal.nsi:panelWidth';

export const DEFAULT_FONT_SIZE = 14;
export const MIN_FONT_SIZE = 8;
export const MAX_FONT_SIZE = 32;

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* stockage indisponible : préférence non persistée */
  }
}

export function getStoredTheme(): ThemeName {
  const value = readStorage(THEME_KEY);
  return value === 'light' || value === 'contrast' || value === 'dark' ? value : 'dark';
}

export function applyTheme(name: ThemeName, term?: Terminal): void {
  document.documentElement.dataset.theme = name;
  writeStorage(THEME_KEY, name);
  if (term) term.options.theme = THEMES[name];
}

export function getStoredFontSize(): number {
  const value = Number.parseInt(readStorage(FONT_SIZE_KEY) ?? '', 10);
  return Number.isFinite(value) && value >= MIN_FONT_SIZE && value <= MAX_FONT_SIZE
    ? value
    : DEFAULT_FONT_SIZE;
}

export function storeFontSize(size: number): void {
  writeStorage(FONT_SIZE_KEY, String(size));
}

/** Colorisation de l'invite, de la saisie et des messages pédagogiques. */
export function getStoredColors(): boolean {
  return readStorage(COLORS_KEY) !== 'off';
}

export function storeColors(enabled: boolean): void {
  writeStorage(COLORS_KEY, enabled ? 'on' : 'off');
}

/** Largeur mémorisée du panneau pédagogique (px). */
export function getStoredPanelWidth(): number | null {
  const value = Number.parseInt(readStorage(PANEL_WIDTH_KEY) ?? '', 10);
  return Number.isFinite(value) ? value : null;
}

export function storePanelWidth(width: number): void {
  writeStorage(PANEL_WIDTH_KEY, String(Math.round(width)));
}

/** Taille de police propre au panneau pédagogique (indépendante de la console). */
export function getStoredPanelFontSize(): number | null {
  const value = Number.parseInt(readStorage(PANEL_FONT_KEY) ?? '', 10);
  return Number.isFinite(value) ? value : null;
}

export function storePanelFontSize(size: number): void {
  writeStorage(PANEL_FONT_KEY, String(size));
}

/** Hauteur mémorisée du panneau pédagogique en mode tablette empilée (px). */
export function getStoredPanelHeight(): number | null {
  const value = Number.parseInt(readStorage(PANEL_HEIGHT_KEY) ?? '', 10);
  return Number.isFinite(value) ? value : null;
}

export function storePanelHeight(height: number): void {
  writeStorage(PANEL_HEIGHT_KEY, String(Math.round(height)));
}
